import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Switch,
  Tooltip,
  IconButton,
  Chip,
  MenuItem,
  FormControlLabel
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
// Componentes
import RemarketingTaskEditor from "../RemarketingTaskEditor";
const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      borderRadius: "16px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
      maxWidth: "800px !important",
      margin: "16px",
    },
  },
  dialogContent: {
    padding: theme.spacing(4),
  },
  headerBox: {
    background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
    padding: theme.spacing(3),
    borderRadius: "12px",
    color: "#fff",
    marginBottom: theme.spacing(4),
    boxShadow: "0 4px 15px rgba(255, 152, 0, 0.2)",
  },
  headerTitle: {
    fontWeight: 800,
    fontSize: "1.5rem",
    letterSpacing: "-0.5px",
  },
  remarketingSection: {
    marginTop: theme.spacing(2),
    border: "1px solid rgba(255, 152, 0, 0.2)",
    borderRadius: "12px",
    padding: theme.spacing(3),
    backgroundColor: "rgba(255, 152, 0, 0.02)",
  },
}));
const FlowBuilderRemarketingModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  // States
  const [activeModal, setActiveModal] = useState(false);
  const [nodeName, setNodeName] = useState("");
  // Remarketing States
  const [remarketingEnabled, setRemarketingEnabled] = useState(true);
  const [remarketingTasks, setRemarketingTasks] = useState([]);
  const [finalTaskEnabled, setFinalTaskEnabled] = useState(true);
  const [finalTaskTime, setFinalTaskTime] = useState(20);
  const [finalTaskUnit, setFinalTaskUnit] = useState("minutes");
  const [finalTaskMessage, setFinalTaskMessage] = useState("");
  const remarketingEditorRef = useRef(null);
  const defaultFinalMessage = i18n.t("flowBuilder.remarketing.defaultFinalMsg") || "Cerrando el ticket por inactividad. ¡Vuelve pronto!";
  useEffect(() => {
    if (open === "edit") {
      setNodeName(data.data.name || "");
      const nodeData = data.data || {};
      setRemarketingEnabled(nodeData.remarketingEnabled !== false);
      let tasks = nodeData.remarketingTasks ? [...nodeData.remarketingTasks] : [];
      let foundFinalTask = null;
      if (tasks.length > 0) {
        const lastTask = tasks[tasks.length - 1];
        if (lastTask.isFinalTask) {
          foundFinalTask = lastTask;
          tasks = tasks.slice(0, tasks.length - 1);
        }
      }
      setRemarketingTasks(tasks);
      if (foundFinalTask) {
        setFinalTaskEnabled(true);
        setFinalTaskTime(foundFinalTask.waitTime || 20);
        setFinalTaskUnit(foundFinalTask.waitUnit || "minutes");
        const textContent = foundFinalTask.content?.find(c => c.type === 'text');
        setFinalTaskMessage(textContent?.value || defaultFinalMessage);
      } else {
        setFinalTaskEnabled(false);
        setFinalTaskTime(20);
        setFinalTaskUnit("minutes");
        setFinalTaskMessage(defaultFinalMessage);
      }
      setActiveModal(true);
    } else if (open === "create") {
      setNodeName("");
      setRemarketingEnabled(true);
      setRemarketingTasks([]);
      setFinalTaskEnabled(true);
      setFinalTaskTime(20);
      setFinalTaskMessage(defaultFinalMessage);
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const handleClose = () => {
    if (remarketingEditorRef.current && remarketingEditorRef.current.hasPendingChanges()) {
      if (!window.confirm(i18n.t("flowBuilder.remarketing.unsavedWarning") || "Tienes cambios sin guardar en el editor de tareas. ¿Descartar?")) {
        return;
      }
    }
    close(null);
    setActiveModal(false);
  };
  const handleSave = (e) => {
    e.preventDefault();
    if (!nodeName.trim()) {
      toast.error("El nombre del nodo es obligatorio");
      return;
    }
    if (remarketingEditorRef.current && remarketingEditorRef.current.hasPendingChanges()) {
      toast.warning(i18n.t("flowBuilder.remarketing.pendingChangesWarning") || "Tienes una tarea de remarketing sin guardar. Por favor, agrégala (o bórrala) antes de guardar el nodo.");
      return;
    }
    // Build final task if enabled
    let allTasks = [...remarketingTasks];
    if (remarketingEnabled && finalTaskEnabled) {
      const finalTaskObj = {
        id: Date.now() + 999123,
        isFinalTask: true,
        isLastTask: true,
        waitTime: Number(finalTaskTime),
        waitUnit: finalTaskUnit || 'minutes',
        variable: `final_closure_${Date.now()}`,
        content: [
          { type: 'text', value: finalTaskMessage || defaultFinalMessage }
        ],
        order: allTasks.length + 1,
        closeTicket: true
      };
      allTasks.push(finalTaskObj);
    }
    const nodeData = {
      name: nodeName,
      type: "remarketing",
      remarketingEnabled,
      remarketingTasks: allTasks
    };
    if (open === "edit") {
      onUpdate({
        ...data,
        data: {
          ...data.data,
          ...nodeData
        }
      });
    } else if (open === "create") {
      onSave(nodeData);
    }
    handleClose();
  };
  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          style: {
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.headerBox}>
            <Typography variant="h5" className={classes.headerTitle}>
              {open === "create" ? "Nuevo Nodo Remarketing" : "Editar Remarketing"}
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.9, marginTop: 4 }}>
              Configura tareas automáticas de seguimiento independientes.
            </Typography>
          </Box>
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <TextField
              label="Nombre del Nodo"
              variant="outlined"
              fullWidth
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              required
            />
            <Box className={classes.remarketingSection}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box>
                  <Typography variant="h6" style={{ fontWeight: 600, color: '#fff' }}>
                    Secuencia de Remarketing
                  </Typography>
                  <Typography variant="body2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Configura mensajes escalonados si el cliente deja de interactuar.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={remarketingEnabled}
                      onChange={(e) => setRemarketingEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={remarketingEnabled ? "Habilitado" : "Deshabilitado"}
                  style={{ margin: 0 }}
                />
              </Box>
              {remarketingEnabled && (
                <>
                  <RemarketingTaskEditor
                    ref={remarketingEditorRef}
                    tasks={remarketingTasks}
                    onChange={setRemarketingTasks}
                    maxTasks={30}
                    allowAuto={false} // [ANTIGRAVITY] SIN INTELIGENCIA ARTIFICIAL EN ESTE NODO
                    enableAntiBan={true}
                  />
                  {/* Tarea de Cierre Obligatoria */}
                  <Box style={{
                    marginTop: '24px',
                    padding: '16px',
                    backgroundColor: 'rgba(211, 47, 47, 0.05)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 107, 107, 1)',
                    boxShadow: '0 0 25px rgba(255, 107, 107, 0.9), inset 0 0 15px rgba(255, 107, 107, 0.3)',
                    position: 'relative'
                  }}>
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" mb={1}>
                      <Box display="flex" flexDirection="row" alignItems="center">
                        <Typography variant="subtitle2" style={{ color: '#ff6b6b', fontWeight: 'bold', marginRight: '8px' }}>
                          {i18n.t("flowBuilder.remarketing.finalTaskTitle") || "Cierre Automatico"}
                        </Typography>
                        <Chip label="Final" size="small" style={{ backgroundColor: '#ff6b6b', color: '#fff', fontWeight: 'bold' }} />
                      </Box>
                      <Switch
                        size="small"
                        checked={finalTaskEnabled}
                        onChange={(e) => setFinalTaskEnabled(e.target.checked)}
                        color="primary"
                      />
                    </Box>
                    {finalTaskEnabled && (
                      <>
                        <Typography variant="body2" style={{ color: '#ff6b6b', marginBottom: '16px', fontSize: '0.8rem', opacity: 0.9 }}>
                          {i18n.t("flowBuilder.remarketing.finalTaskDesc") || "Si no hay respuesta tras el ultimo intento, el ticket se cerrara automaticamente."}
                        </Typography>
                        <Box display="flex" flexDirection="column" style={{ gap: '20px' }}>
                          <Box display="flex" flexDirection="row" alignItems="center" style={{ gap: '10px' }}>
                            <TextField
                              label={i18n.t("flowBuilder.remarketing.waitTime") || "Espera Final"}
                              type="number"
                              variant="outlined"
                              size="small"
                              value={finalTaskTime}
                              onChange={(e) => {
                                let val = parseInt(e.target.value);
                                if (val < 1) val = 1;
                                if (val > 360) val = 360;
                                setFinalTaskTime(val);
                              }}
                              InputProps={{ inputProps: { min: 1, max: 360 } }}
                              style={{ width: '150px' }}
                            />
                            <TextField
                              select
                              SelectProps={{ MenuProps: { PaperProps: { style: { backgroundColor: "#424242", color: "white" } } } }}
                              label={i18n.t("flowBuilder.remarketing.waitUnit") || "Unidad"}
                              variant="outlined"
                              size="small"
                              value={finalTaskUnit}
                              onChange={(e) => setFinalTaskUnit(e.target.value)}
                              style={{ width: '150px' }}
                            >
                              <MenuItem value="minutes">{i18n.t("flowBuilder.remarketing.units.minutes") || "Minutos"}</MenuItem>
                              <MenuItem value="hours">{i18n.t("flowBuilder.remarketing.units.hours") || "Horas"}</MenuItem>
                              <MenuItem value="days">{i18n.t("flowBuilder.remarketing.units.days") || "Dias"}</MenuItem>
                            </TextField>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>{i18n.t("flowBuilder.remarketing.afterLastAction") || "despues de la ultima accion."}</Typography>
                          </Box>
                          <Box display="flex" flexDirection="row" alignItems="center" style={{ width: '100%' }}>
                            <TextField
                              label={i18n.t("flowBuilder.remarketing.finalTaskMessageLabel") || "Mensaje de Cierre"}
                              type="text"
                              variant="outlined"
                              size="small"
                              fullWidth
                              multiline
                              maxRows={4}
                              value={finalTaskMessage}
                              onChange={(e) => setFinalTaskMessage(e.target.value)}
                              placeholder="Ej: Cerramos el ticket por inactividad..."
                            />
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                </>
              )}
            </Box>
            <DialogActions style={{ padding: '24px 0 0 0', marginTop: 'auto' }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                style={{
                  borderRadius: "8px",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontWeight: "bold",
                  borderColor: "#ff4d4f",
                  color: "#ff4d4f",
                  marginRight: 8
                }}
              >
                {i18n.t("common.cancel") || "Cancelar"}
              </Button>
              <Button
                type="submit"
                variant="contained"
                style={{
                  borderRadius: "8px",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)"
                }}
              >
                {open === "create" ? "Añadir" : "Guardar"}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default FlowBuilderRemarketingModal;
